// API Client для работы с сервером
// Заменяет localStorage на серверное хранилище

class APIClient {
    constructor(baseURL = '') {
        this.baseURL = baseURL;
        this.autosaveTimeout = null;
        this.currentEstimateFilename = null;
    }

    // ============ Каталог (v3 Migration - Server-based) ============

    /**
     * Get list of catalogs for current user's organization.
     *
     * Sprint 20w perf: in-flight dedup + 30s TTL cache.
     * Раньше делалось 2 GET /api/v1/catalogs параллельно при boot (entry.js + section).
     * Теперь параллельные вызовы шарят один Promise, повторные в течение 30s — cached.
     *
     * @returns {Promise<{success: boolean, data: {catalogs: Array}}>}
     */
    async getCatalogsList() {
        const TTL_MS = 30000;
        const now = Date.now();
        // Если есть свежий кэш — отдаём его
        if (this._catalogsCache && (now - this._catalogsCacheAt) < TTL_MS) {
            return this._catalogsCache;
        }
        // Если уже идёт запрос — шарим Promise (in-flight dedup)
        if (this._catalogsInFlight) {
            return this._catalogsInFlight;
        }
        this._catalogsInFlight = (async () => {
            try {
                const response = await this._fetchWithAuth('/api/v1/catalogs', { method: 'GET' });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                const json = await response.json();
                this._catalogsCache = json;
                this._catalogsCacheAt = Date.now();
                return json;
            } finally {
                this._catalogsInFlight = null;
            }
        })();
        return this._catalogsInFlight;
    }

    /** Sprint 20w perf: invalidate catalogs cache (после create/update/delete каталогa). */
    _invalidateCatalogsCache() {
        this._catalogsCache = null;
        this._catalogsCacheAt = 0;
    }

    /**
     * Sprint 20w perf Phase 3: generic cache helper.
     * - TTL 30s (для list endpoints)
     * - In-flight Promise dedup (parallel requests share)
     * - invalidate() по namespace на mutations
     *
     * Использование:
     *   return this._cachedFetch('clients', cacheKey, async () => {...fetch...});
     */
    _cachedFetch(namespace, key, fetcher, ttlMs = 30000) {
        if (!this._cache) this._cache = new Map();
        const fullKey = `${namespace}:${key}`;
        const cached = this._cache.get(fullKey);
        const now = Date.now();
        if (cached && (now - cached.at) < ttlMs && !cached.inFlight) {
            return Promise.resolve(cached.value);
        }
        if (cached && cached.inFlight) {
            return cached.inFlight;
        }
        const inFlight = (async () => {
            try {
                const value = await fetcher();
                this._cache.set(fullKey, { value, at: Date.now(), inFlight: null });
                return value;
            } catch (err) {
                this._cache.delete(fullKey);
                throw err;
            }
        })();
        this._cache.set(fullKey, { ...(cached || {}), inFlight });
        return inFlight;
    }

    /** Invalidate all cache entries в namespace (clients/suppliers/estimates/tasks). */
    _invalidateCache(namespace) {
        if (!this._cache) return;
        for (const k of this._cache.keys()) {
            if (k.startsWith(namespace + ':')) this._cache.delete(k);
        }
    }

    /**
     * Sprint 20w perf: combined initial-data endpoint. ~1 round-trip vs 5-9.
     *
     * Returns: { user, organization, currencies, catalogs, suppliers,
     *            clients_last_update, system_flags, server_time }
     *
     * Side-effect: prefills _catalogsCache + _currenciesCache → последующие
     * `getCatalogsList()` / `listCurrencies()` используют кэш без round-trip.
     */
    async bootstrap() {
        const r = await this._fetchWithAuth('/api/v1/bootstrap', { method: 'GET' });
        if (!r.ok) throw new Error(`bootstrap failed: ${r.status}`);
        const json = await r.json();
        if (!json.success) throw new Error(json.error || 'bootstrap returned success=false');
        const data = json.data;

        // Prefill caches — экономит 270ms × 2 (catalogs + currencies) на section nav
        if (data.catalogs) {
            this._catalogsCache = { success: true, data: { catalogs: data.catalogs } };
            this._catalogsCacheAt = Date.now();
        }
        // Sprint 20w fix: prefill cache ТОЛЬКО если master-list непустой.
        // Иначе empty bootstrap response (например при backend error) поломает
        // последующие listCurrencies() — cache hit с empty arrays → UI "Нет валют".
        if (data.currencies && Array.isArray(data.currencies.currencies) && data.currencies.currencies.length > 0) {
            this._currenciesCache = data.currencies;
            this._currenciesCacheAt = Date.now();
        }
        // Sprint 25 perf: bootstrap_version >= 2 → prefill _cachedFetch Map.
        // `getEstimatesList()` (key estimates:all_1000) и list-эндпоинты читают
        // из этой Map'ы — поэтому на cold load 4-5 round-trip'ов не происходит.
        if (data._bootstrap_version >= 2) {
            if (!this._cache) this._cache = new Map();
            const now = Date.now();
            if (Array.isArray(data.estimates_index)) {
                this._cache.set('estimates:all_1000', {
                    value: data.estimates_index,
                    at: now,
                    inFlight: null
                });
            }
            if (data.clients_meta) {
                this._cache.set('clients:tag-cloud', {
                    value: data.clients_meta.tag_cloud,
                    at: now,
                    inFlight: null
                });
                this._cache.set('clients:source-counts', {
                    value: data.clients_meta.source_counts,
                    at: now,
                    inFlight: null
                });
            }
            if (Array.isArray(data.settings)) {
                this._cache.set('settings:all', {
                    value: data.settings,
                    at: now,
                    inFlight: null
                });
            }
            if (Array.isArray(data.partners)) {
                this._cache.set('partners:active', {
                    value: data.partners,
                    at: now,
                    inFlight: null
                });
            }
        }
        return data;
    }

    // Sprint 19d: server-first regions/categories (заменяет localStorage)
    async listRegions() {
        const r = await this._fetchWithAuth('/api/v1/catalogs/regions/list', { method: 'GET' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()).data || [];
    }
    async deleteRegion(name) {
        const r = await this._fetchWithAuth(`/api/v1/catalogs/regions/${encodeURIComponent(name)}`, { method: 'DELETE' });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async renameRegion(from, to) {
        const r = await this._fetchWithAuth('/api/v1/catalogs/regions/rename', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ from, to })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async listRegionCategories(region) {
        const r = await this._fetchWithAuth(`/api/v1/catalogs/regions/${encodeURIComponent(region)}/categories`, { method: 'GET' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()).data || [];
    }
    async setRegionCategories(region, categories) {
        const r = await this._fetchWithAuth(`/api/v1/catalogs/regions/${encodeURIComponent(region)}/categories`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ categories })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async createRegion(name, defaultCategories = []) {
        // Создаём catalog для нового региона
        const data = { region: name, templates: [], categories: defaultCategories };
        const r = await this._fetchWithAuth('/api/v1/catalogs', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, data, visibility: 'organization' })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }

    /**
     * Load full catalog data by ID
     * @param {string} id - Catalog UUID
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async loadCatalogById(id) {
        const response = await this._fetchWithAuth(`/api/v1/catalogs/${id}`, { method: 'GET' });
        if (!response.ok) {
            if (response.status === 404) throw new Error('Catalog not found');
            if (response.status === 401) throw new Error('Authentication required');
            if (response.status === 403) throw new Error('Access denied');
            throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
    }

    /**
     * Create or update catalog
     * @param {string} name - Catalog name
     * @param {Object} data - Catalog data (templates, categories)
     * @param {string} visibility - 'organization'|'public'|'private'
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async saveCatalog(name, data, visibility = 'organization') {
        const response = await this._fetchWithAuth('/api/v1/catalogs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, data, visibility })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || 'Save failed');
        }
        this._invalidateCatalogsCache();  // Sprint 20w perf: invalidate cache
        return response.json();
    }

    /**
     * Refresh access token using httpOnly refresh_token cookie.
     * Returns true if successful (server set new cookies), false otherwise.
     * Параллельные refresh-запросы делятся одним promise (защита от storm).
     *
     * Sprint 15 C.1 (2026-05-09): legacy localStorage Bearer fallback удалён.
     * Refresh-token живёт ТОЛЬКО в httpOnly cookie на path=/api/v1/auth.
     */
    async _refreshAccessToken() {
        if (this._refreshInFlight) return this._refreshInFlight;

        this._refreshInFlight = (async () => {
            try {
                const response = await fetch(`${this.baseURL}/api/v1/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    // Body пустой — backend читает refresh_token из httpOnly cookie.
                    body: JSON.stringify({}),
                    credentials: 'include'
                });

                // Sprint 18 fix: 429 (rate-limit) — temporary, NOT logout trigger.
                // Может случиться при множественных tabs/parallel requests.
                // Возвращаем 'rate-limited' чтобы caller знал — это особый случай.
                if (response.status === 429) {
                    console.warn('[APIClient] Refresh rate-limited (429) — будем retry позже');
                    return 'rate-limited';
                }

                if (!response.ok) return false;
                const result = await response.json();
                // Backend сам устанавливает новые cookies через setAuthCookies();
                // нам не нужно ничего сохранять локально.
                return result.success === true;
            } catch (err) {
                console.error('[APIClient] Refresh failed:', err);
                return false;
            }
        })();

        try {
            return await this._refreshInFlight;
        } finally {
            this._refreshInFlight = null;
        }
    }

    /**
     * Если 401 случилось и refresh не помог — выкидываем на login.
     * Защита от спама: redirect только один раз за сессию страницы.
     *
     * Sprint 18 fix цикла login↔main:
     * - Очищаем auth_present cookie ВРУЧНУЮ (JS-readable) чтобы login.js
     *   видел "no auth" и не редиректил обратно на /
     * - Backend's httpOnly access_token + refresh_token либо expired либо
     *   revoked — мы их JS не очистим, но они не блокируют login flow
     *   (login.html → POST /auth/login → backend выставит свежие cookies)
     */
    _redirectToLogin() {
        if (this._loginRedirectFired) return;
        this._loginRedirectFired = true;

        // КРИТИЧНО: clear auth_present cookie на всех paths иначе login.js
        // увидит её, посчитает юзера залогиненным и редиректнет обратно → ∞ loop
        try {
            const expire = '; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            document.cookie = 'auth_present=; path=/' + expire;
            document.cookie = 'auth_present=; path=/api/v1/auth' + expire;
            // Подметаем legacy ключи (Sprint 15 C.1 cleanup)
            localStorage.removeItem('jwt_token');
            localStorage.removeItem('jwt_refresh_token');
            localStorage.removeItem('authToken');
        } catch (_) { /* ignore */ }

        const next = encodeURIComponent(location.pathname + location.search);
        location.replace('/login.html?next=' + next);
    }

    /**
     * Wrapper для fetch с автоматическим refresh при 401.
     * Sprint 15 C.1: auth теперь ТОЛЬКО через httpOnly cookies. Никаких
     * localStorage tokens, никакого Authorization Bearer header.
     */
    async _fetchWithAuth(url, init = {}, _retried = false) {
        // _optionalAuth flag оставлен для совместимости (legacy ручки),
        // но семантика изменилась: cookies всё равно отправляются если есть.
        const optional = init._optionalAuth === true;

        const finalInit = {
            ...init,
            // Sprint 11+15: httpOnly access_token + refresh_token едут в cookies.
            credentials: 'include',
            headers: { ...(init.headers || {}) }
        };
        delete finalInit._optionalAuth;

        const response = await fetch(`${this.baseURL}${url}`, finalInit);

        if (response.status === 401 && !_retried && !optional) {
            // Парсим body чтобы решить — refresh пытаться или сразу logout.
            let body = {};
            try { body = await response.clone().json(); } catch (_) { /* not json */ }
            // Sprint 15 C.1 fix: NO_TOKEN тоже триггерит refresh (access_token cookie
            // истёк → browser сам удалил → backend видит NO_TOKEN, но refresh_token
            // ещё может быть валиден). REFRESH_REVOKED → сразу logout (refresh умер).
            const code = body.code;
            const tryRefresh = !code ||
                code === 'TOKEN_EXPIRED' ||
                code === 'INVALID_TOKEN' ||
                code === 'NO_TOKEN' ||
                code === 'WRONG_TYPE';
            if (tryRefresh) {
                const refreshResult = await this._refreshAccessToken();
                if (refreshResult === true) {
                    return this._fetchWithAuth(url, init, true);
                }
                // Sprint 18 fix: 'rate-limited' (429) — НЕ logout, просто оригинальный 401
                // Caller (UI) увидит 401 и решит что показать. На next click refresh
                // может уже сработать (rate window прошёл).
                if (refreshResult === 'rate-limited') {
                    return response;
                }
                // false — refresh реально умер (revoked / network error / 4xx).
                // Cookie auth сломана — на login.
                this._redirectToLogin();
            } else {
                // Любой другой 401 (REFRESH_REVOKED, AUTH_FAILED) → сразу на login.
                this._redirectToLogin();
            }
        }

        return response;
    }

    // ============ Сметы ============
    // GET-эндпоинты на /api/v1 (require JWT, multi-tenancy через RBAC).
    // saveEstimate использует legacy POST /api/estimates/:id (upsert по ID),
    // потому что v1 POST / генерирует свой ID, а нам нужно сохранять
    // существующий UUID сметы. Когда v1 добавит "create with given id" — переключим.

    async getEstimatesList() {
        // Sprint 20w perf: cache + dedup. estimates?limit=1000 вызывается из 4 мест
        // (boot, /#/tracker, /#/estimates, /#/clients) — раньше 4 round-trips.
        return this._cachedFetch('estimates', 'all_1000', async () => {
            const response = await this._fetchWithAuth('/api/v1/estimates?limit=1000', { method: 'GET' });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const result = await response.json();
            if (!result.success) throw new Error(result.error);
            return result.data?.estimates || result.estimates || [];
        });
    }

    async getEstimate(id) {
        const response = await this._fetchWithAuth(`/api/v1/estimates/${id}`, { method: 'GET' });
        if (!response.ok) {
            if (response.status === 404) throw new Error('Estimate not found');
            if (response.status === 403) throw new Error('Access denied');
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        return result.data;
    }

    async loadEstimate(filename) {
        // Legacy: загрузка по filename. _optionalAuth — на случай если бэкенд
        // временно отдаст без auth (пока legacy ручка). Но всё же шлём токен если есть.
        const response = await this._fetchWithAuth(`/api/estimates/${filename}`, {
            method: 'GET',
            _optionalAuth: true
        });
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        this.currentEstimateFilename = filename;
        return result.data;
    }

    async saveEstimate(id, data) {
        if (!id) throw new Error('ID is required for saveEstimate');

        const response = await this._fetchWithAuth(`/api/estimates/${id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            _optionalAuth: true   // legacy ручка ещё пускает без auth — OK
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to save estimate: ${response.status} ${errorText}`);
        }

        const result = await response.json();
        if (!result.success) throw new Error(result.error);

        this.currentEstimateId = id;
        this._invalidateCache('estimates');  // Sprint 20w perf
        return { ...result, id };
    }

    // ============================================================================
    // Backups v2 — снапшоты сметы (отдельная таблица estimate_backups)
    // ============================================================================

    /**
     * Создать snapshot-бэкап сметы.
     * @param {string} estimateId — UUID сметы
     * @param {Object} data — full estimate data
     * @param {Object} [opts]
     * @param {string} [opts.reason='autosave']
     * @param {string} [opts.filename]
     * @param {number} [opts.pruneKeep=10] — оставить только N последних бэкапов
     */
    async createBackup(estimateId, data, opts = {}) {
        const response = await this._fetchWithAuth('/api/v1/backups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                estimate_id: estimateId,
                data,
                reason: opts.reason || 'autosave',
                filename: opts.filename,
                prune_keep: opts.pruneKeep ?? 10
            })
        });
        if (!response.ok) {
            const txt = await response.text();
            throw new Error(`createBackup failed: ${response.status} ${txt}`);
        }
        const result = await response.json();
        return result.data;
    }

    /**
     * Список бэкапов организации.
     * @param {Object} [opts]
     * @param {string} [opts.estimateId] — backups одной сметы
     * @param {boolean} [opts.orphanOnly] — только orphan backups
     * @param {number} [opts.limit=50]
     * @param {number} [opts.offset=0]
     */
    async listBackups(opts = {}) {
        const params = new URLSearchParams();
        if (opts.estimateId) params.set('estimate_id', opts.estimateId);
        if (opts.orphanOnly) params.set('orphan_only', 'true');
        params.set('limit', String(opts.limit ?? 50));
        params.set('offset', String(opts.offset ?? 0));

        const response = await this._fetchWithAuth(`/api/v1/backups?${params.toString()}`, {
            method: 'GET'
        });
        if (!response.ok) {
            throw new Error(`listBackups failed: ${response.status}`);
        }
        const result = await response.json();
        return result.data;
    }

    /**
     * Получить полный snapshot бэкапа.
     */
    async getBackup(backupId) {
        const response = await this._fetchWithAuth(`/api/v1/backups/${backupId}`, {
            method: 'GET'
        });
        if (!response.ok) {
            throw new Error(`getBackup failed: ${response.status}`);
        }
        const result = await response.json();
        return result.data;
    }

    /**
     * Удалить бэкап.
     */
    async deleteBackup(backupId) {
        const response = await this._fetchWithAuth(`/api/v1/backups/${backupId}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            throw new Error(`deleteBackup failed: ${response.status}`);
        }
        return true;
    }

    /**
     * Восстановить estimate из бэкапа.
     */
    async restoreBackup(backupId) {
        const response = await this._fetchWithAuth(`/api/v1/backups/${backupId}/restore`, {
            method: 'POST'
        });
        if (!response.ok) {
            throw new Error(`restoreBackup failed: ${response.status}`);
        }
        const result = await response.json();
        return result.data;
    }

    // ============================================================================
    // Clients (CRM Phase 3, 2026-05-08)
    // ============================================================================

    async listClients(opts = {}) {
        const params = new URLSearchParams();
        if (opts.q) params.set('q', opts.q);
        if (opts.sort) params.set('sort', opts.sort);
        if (opts.dir) params.set('dir', opts.dir);
        params.set('limit', String(opts.limit ?? 200));
        params.set('offset', String(opts.offset ?? 0));
        const cacheKey = params.toString();
        // Sprint 20w perf: cache + dedup для повторных вызовов в течение 30s
        return this._cachedFetch('clients', cacheKey, async () => {
            const r = await this._fetchWithAuth(`/api/v1/clients?${cacheKey}`, { method: 'GET' });
            if (!r.ok) throw new Error(`listClients failed: ${r.status}`);
            return (await r.json()).data;
        });
    }

    async getClient(id) {
        const r = await this._fetchWithAuth(`/api/v1/clients/${id}`, { method: 'GET' });
        if (!r.ok) throw new Error(`getClient failed: ${r.status}`);
        return (await r.json()).data;
    }

    async createClient(data) {
        const r = await this._fetchWithAuth('/api/v1/clients', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`createClient failed: ${r.status} ${await r.text()}`);
        this._invalidateCache('clients');
        return (await r.json()).data;
    }

    async updateClient(id, data) {
        const r = await this._fetchWithAuth(`/api/v1/clients/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`updateClient failed: ${r.status}`);
        this._invalidateCache('clients');
        return (await r.json()).data;
    }

    async deleteClient(id) {
        const r = await this._fetchWithAuth(`/api/v1/clients/${id}`, { method: 'DELETE' });
        if (!r.ok) throw new Error(`deleteClient failed: ${r.status}`);
        this._invalidateCache('clients');
        return true;
    }

    // ============================================================================
    // Suppliers (CRM Phase 4, 2026-05-08)
    // ============================================================================

    async listSuppliers(opts = {}) {
        const params = new URLSearchParams();
        if (opts.q) params.set('q', opts.q);
        if (opts.type) params.set('type', opts.type);
        if (opts.incompleteOnly) params.set('incomplete_only', 'true');
        if (opts.sort) params.set('sort', opts.sort);
        if (opts.dir) params.set('dir', opts.dir);
        params.set('limit', String(opts.limit ?? 200));
        params.set('offset', String(opts.offset ?? 0));
        const cacheKey = params.toString();
        // Sprint 20w perf: cache + dedup
        return this._cachedFetch('suppliers', cacheKey, async () => {
            const r = await this._fetchWithAuth(`/api/v1/suppliers?${cacheKey}`, { method: 'GET' });
            if (!r.ok) throw new Error(`listSuppliers failed: ${r.status}`);
            const json = await r.json();
            return json.data || {};
        });
    }

    async getSupplier(id) {
        const r = await this._fetchWithAuth(`/api/v1/suppliers/${id}`, { method: 'GET' });
        if (!r.ok) throw new Error(`getSupplier failed: ${r.status}`);
        return (await r.json()).data;
    }

    async createSupplier(data) {
        const r = await this._fetchWithAuth('/api/v1/suppliers', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`createSupplier failed: ${r.status}`);
        this._invalidateCache('suppliers');
        return (await r.json()).data;
    }

    async quickCreateSupplier(companyName) {
        const r = await this._fetchWithAuth('/api/v1/suppliers/quick', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ company_name: companyName })
        });
        if (!r.ok) throw new Error(`quickCreateSupplier failed: ${r.status}`);
        this._invalidateCache('suppliers');
        return (await r.json()).data;
    }

    async updateSupplier(id, data) {
        const r = await this._fetchWithAuth(`/api/v1/suppliers/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`updateSupplier failed: ${r.status}`);
        this._invalidateCache('suppliers');
        return (await r.json()).data;
    }

    async deleteSupplier(id) {
        const r = await this._fetchWithAuth(`/api/v1/suppliers/${id}`, { method: 'DELETE' });
        if (!r.ok) throw new Error(`deleteSupplier failed: ${r.status}`);
        this._invalidateCache('suppliers');
        return true;
    }

    // ============================================================================
    // Templates / Прайсы (CRM Phase 5, 2026-05-08)
    // Bulk-операции над templates across catalogs.
    // ============================================================================

    async listAllTemplates(opts = {}) {
        const params = new URLSearchParams();
        if (opts.q) params.set('q', opts.q);
        if (opts.region) params.set('region', opts.region);
        if (opts.category) params.set('category', opts.category);
        if (opts.supplier) params.set('supplier', opts.supplier);
        if (opts.sort) params.set('sort', opts.sort);
        if (opts.dir) params.set('dir', opts.dir);
        params.set('limit', String(opts.limit ?? 500));
        params.set('offset', String(opts.offset ?? 0));
        const r = await this._fetchWithAuth(`/api/v1/templates?${params.toString()}`, { method: 'GET' });
        if (!r.ok) throw new Error(`listAllTemplates failed: ${r.status}`);
        return (await r.json()).data;
    }

    async updateTemplate(catalogId, templateId, data) {
        const r = await this._fetchWithAuth(
            `/api/v1/templates/${encodeURIComponent(catalogId)}/${encodeURIComponent(templateId)}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );
        if (!r.ok) throw new Error(`updateTemplate failed: ${r.status} ${await r.text()}`);
        return (await r.json()).data;
    }

    async deleteTemplate(catalogId, templateId) {
        const r = await this._fetchWithAuth(
            `/api/v1/templates/${encodeURIComponent(catalogId)}/${encodeURIComponent(templateId)}`,
            { method: 'DELETE' }
        );
        if (!r.ok) throw new Error(`deleteTemplate failed: ${r.status}`);
        return (await r.json()).data;
    }

    // ============================================================================
    // Dashboard (CRM Phase 6, 2026-05-08)
    // ============================================================================

    async getDashboardStats() {
        const r = await this._fetchWithAuth('/api/v1/dashboard/stats', { method: 'GET' });
        if (!r.ok) throw new Error(`getDashboardStats failed: ${r.status}`);
        return (await r.json()).data;
    }

    // ============================================================================
    // Tasks / Tracker (CRM Phase 7, 2026-05-08)
    // ============================================================================

    async listTasks(opts = {}) {
        const params = new URLSearchParams();
        if (opts.status) params.set('status', opts.status);
        if (opts.type) params.set('type', opts.type);
        if (opts.estimate_id) params.set('estimate_id', opts.estimate_id);
        if (opts.upcoming) params.set('upcoming', 'true');
        if (opts.sort) params.set('sort', opts.sort);
        if (opts.dir) params.set('dir', opts.dir);
        params.set('limit', String(opts.limit ?? 200));
        params.set('offset', String(opts.offset ?? 0));
        const cacheKey = params.toString();
        return this._cachedFetch('tasks', cacheKey, async () => {
            const r = await this._fetchWithAuth(`/api/v1/tasks?${cacheKey}`, { method: 'GET' });
            if (!r.ok) throw new Error(`listTasks failed: ${r.status}`);
            return (await r.json()).data;
        });
    }

    async getTask(id) {
        const r = await this._fetchWithAuth(`/api/v1/tasks/${id}`, { method: 'GET' });
        if (!r.ok) throw new Error(`getTask failed: ${r.status}`);
        return (await r.json()).data;
    }

    async createTask(data) {
        const r = await this._fetchWithAuth('/api/v1/tasks', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`createTask failed: ${r.status}`);
        this._invalidateCache('tasks');
        return (await r.json()).data;
    }

    async updateTask(id, data) {
        const r = await this._fetchWithAuth(`/api/v1/tasks/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`updateTask failed: ${r.status}`);
        this._invalidateCache('tasks');
        return (await r.json()).data;
    }

    async completeTask(id) {
        const r = await this._fetchWithAuth(`/api/v1/tasks/${id}/complete`, { method: 'POST' });
        if (!r.ok) throw new Error(`completeTask failed: ${r.status}`);
        this._invalidateCache('tasks');
        return (await r.json()).data;
    }

    async deleteTask(id) {
        const r = await this._fetchWithAuth(`/api/v1/tasks/${id}`, { method: 'DELETE' });
        if (!r.ok) throw new Error(`deleteTask failed: ${r.status}`);
        this._invalidateCache('tasks');
        return true;
    }

    // ============================================================================
    // Inbox / Quick-entry (Sprint 18 №4, 2026-05-09)
    // ============================================================================

    /**
     * Quick add в inbox. rawText может быть multiline — server разбивает по \n
     * на отдельные entries (skip empty lines).
     * @param {string} rawText
     * @returns {Promise<{created:number, ids:string[]}>}
     */
    async addToInbox(rawText) {
        const r = await this._fetchWithAuth('/api/v1/inbox', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ raw_text: rawText })
        });
        if (!r.ok) throw new Error(`addToInbox failed: ${r.status}`);
        return (await r.json()).data;
    }

    /**
     * @param {{status?:'unprocessed'|'all', limit?:number, offset?:number}} [opts]
     * @returns {Promise<{entries:Array, count:number, stats:object, limit:number, offset:number}>}
     */
    async listInbox(opts = {}) {
        const params = new URLSearchParams();
        if (opts.status) params.set('status', opts.status);
        params.set('limit', String(opts.limit ?? 50));
        params.set('offset', String(opts.offset ?? 0));
        const r = await this._fetchWithAuth(`/api/v1/inbox?${params.toString()}`, { method: 'GET' });
        if (!r.ok) throw new Error(`listInbox failed: ${r.status}`);
        return (await r.json()).data;
    }

    /**
     * Soft-dismiss inbox entry (NOT permanent delete — только sets dismissed_at).
     * @param {string} id
     */
    async dismissInbox(id) {
        const r = await this._fetchWithAuth(`/api/v1/inbox/${encodeURIComponent(id)}`, {
            method: 'DELETE'
        });
        if (!r.ok) throw new Error(`dismissInbox failed: ${r.status}`);
        return true;
    }

    /**
     * Manual convert inbox → task (без Claude). Server создаёт task и marks
     * inbox entry parsed.
     * @param {string} id
     * @param {{title?:string, type?:string, priority?:string, due_date?:string|number, description?:string}} taskData
     * @returns {Promise<{task_id:string, inbox_id:string}>}
     */
    async convertInboxToTask(id, taskData = {}) {
        const r = await this._fetchWithAuth(
            `/api/v1/inbox/${encodeURIComponent(id)}/convert-to-task`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(taskData)
            }
        );
        if (!r.ok) throw new Error(`convertInboxToTask failed: ${r.status}`);
        return (await r.json()).data;
    }

    // ============================================================================
    // Currencies (CRM Phase 8b, 2026-05-07)
    // ============================================================================

    /**
     * Список валют (master) + rates + org_settings для текущей org.
     *
     * Sprint 20w perf: 60s in-memory cache + in-flight dedup. Currencies+rates
     * меняются редко (1-2 раза в день), org_settings на org-level.
     * `_invalidateCurrenciesCache()` вызывается после updateRate/lockRate.
     *
     * @returns {Promise<{currencies, rates, org_settings}>}
     */
    async listCurrencies() {
        const TTL_MS = 60000;
        const now = Date.now();
        if (this._currenciesCache && (now - this._currenciesCacheAt) < TTL_MS) {
            return this._currenciesCache;
        }
        if (this._currenciesInFlight) return this._currenciesInFlight;
        this._currenciesInFlight = (async () => {
            try {
                const r = await this._fetchWithAuth('/api/v1/currencies', { method: 'GET' });
                if (!r.ok) throw new Error(`listCurrencies failed: ${r.status}`);
                const data = (await r.json()).data;
                this._currenciesCache = data;
                this._currenciesCacheAt = Date.now();
                return data;
            } finally {
                this._currenciesInFlight = null;
            }
        })();
        return this._currenciesInFlight;
    }

    _invalidateCurrenciesCache() {
        this._currenciesCache = null;
        this._currenciesCacheAt = 0;
    }

    /**
     * Manual обновление курса from→to. source автоматически = 'manual'.
     * @param {string} from
     * @param {string} to
     * @param {number} rate
     * @param {boolean} [isLocked]  optional — ставит is_locked
     */
    async updateRate(from, to, rate, isLocked) {
        const body = { rate };
        if (typeof isLocked === 'boolean') body.is_locked = isLocked;
        const r = await this._fetchWithAuth(
            `/api/v1/currencies/rate/${encodeURIComponent(from)}/${encodeURIComponent(to)}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            }
        );
        if (!r.ok) throw new Error(`updateRate failed: ${r.status}`);
        this._invalidateCurrenciesCache();  // Sprint 20w perf
        return (await r.json()).data;
    }

    /**
     * Toggle is_locked для пары from→to.
     */
    async toggleRateLock(from, to) {
        const r = await this._fetchWithAuth(
            `/api/v1/currencies/lock/${encodeURIComponent(from)}/${encodeURIComponent(to)}`,
            { method: 'PUT' }
        );
        if (!r.ok) throw new Error(`toggleRateLock failed: ${r.status}`);
        this._invalidateCurrenciesCache();
        return (await r.json()).data;
    }

    /**
     * Обновить per-currency org settings (is_active / fee / default).
     * @param {string} code
     * @param {{is_active?:boolean, conversion_fee_percent?:number, is_default_client?:boolean}} settings
     */
    async updateCurrencySettings(code, settings) {
        const r = await this._fetchWithAuth(
            `/api/v1/currencies/settings/${encodeURIComponent(code)}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings || {})
            }
        );
        if (!r.ok) throw new Error(`updateCurrencySettings failed: ${r.status}`);
        return (await r.json()).data;
    }

    /**
     * Placeholder: запросить auto-refresh курсов из внешних API.
     * Сейчас возвращает success=true с message что не реализовано.
     */
    async refreshRates() {
        const r = await this._fetchWithAuth('/api/v1/currencies/refresh-rates', {
            method: 'POST'
        });
        if (!r.ok) throw new Error(`refreshRates failed: ${r.status}`);
        return r.json();
    }

    async saveBatch(items) {
        const response = await this._fetchWithAuth('/api/estimates/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
            _optionalAuth: true
        });
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        return result;
    }

    async deleteEstimate(id) {
        const response = await this._fetchWithAuth(`/api/v1/estimates/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to delete estimate: ${response.status} ${errorText}`);
        }
        const result = await response.json();
        if (!result.success) throw new Error(result.error);

        this._invalidateCache('estimates');  // Sprint 20w perf
        if (this.currentEstimateId === id) this.currentEstimateId = null;
        return result;
    }

    async renameEstimate(id, newFilename) {
        const response = await this._fetchWithAuth(`/api/v1/estimates/${id}/rename`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: newFilename, newFilename })
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to rename estimate: ${response.status} ${errorText}`);
        }
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        return result;
    }

    // Автосохранение с debounce (8 секунд)
    // Сохраняет смету в базу данных (estimates таблица)
    // ID-First: используем ID сметы как ключ, не filename
    scheduleAutosave(data, filename) {
        if (!data.id) {
            return; // Не автосохраняем несохраненные сметы (ID обязателен)
        }

        if (this.autosaveTimeout) {
            clearTimeout(this.autosaveTimeout);
        }

        this.autosaveTimeout = setTimeout(async () => {
            try {
                // ID-First: сохраняем по ID, не по filename
                await this.saveEstimate(data.id, data);
                // Автосохранение тихое - не логируем
            } catch (err) {
                // Только критичные ошибки логируем
                console.error('Autosave failed:', err);
            }
        }, 8000); // 8 секунд - оптимальный баланс между безопасностью данных и нагрузкой на сервер
    }

    // ============ Настройки ============

    async loadSettings() {
        const response = await this._fetchWithAuth('/api/settings', {
            method: 'GET',
            _optionalAuth: true
        });
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        return result.data;
    }

    async saveSettings(data) {
        const response = await this._fetchWithAuth('/api/settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            _optionalAuth: true
        });
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        return result;
    }

    // ============ Export/Import (Data Portability) ============

    async exportAll(includeBackups = true) {
        const response = await this._fetchWithAuth(
            `/api/export/all?includeBackups=${includeBackups}`,
            { method: 'GET', _optionalAuth: true }
        );
        if (!response.ok) {
            const result = await response.json().catch(() => ({}));
            throw new Error(result.error || 'Export failed');
        }
        return await response.json();
    }

    async importAll(importData) {
        const response = await this._fetchWithAuth('/api/import/all', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(importData),
            _optionalAuth: true
        });
        const result = await response.json();
        if (!result.success) throw new Error(result.error);
        return result;
    }

    async exportDatabase() {
        const response = await this._fetchWithAuth('/api/export/database', {
            method: 'GET',
            _optionalAuth: true
        });

        if (!response.ok) {
            // Try to parse error message
            try {
                const result = await response.json();
                throw new Error(result.error || 'Database export failed');
            } catch (e) {
                throw new Error(`Database export failed: ${response.status} ${response.statusText}`);
            }
        }

        return await response.blob();
    }

    /**
     * Helper: Download blob as file (triggers browser download)
     * @param {Blob} blob - File blob
     * @param {string} filename - Suggested filename
     */
    downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Helper: Download JSON object as file
     * @param {Object} data - JSON data
     * @param {string} filename - Suggested filename
     */
    downloadJSON(data, filename) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        this.downloadBlob(blob, filename);
    }

    // ============ Generic HTTP Methods (для SyncManager) ============

    /**
     * Generic GET request
     * @param {string} endpoint - API endpoint path
     * @param {Object} options - Fetch options
     * @returns {Promise<any>}
     */
    async get(endpoint, options = {}) {
        const response = await this._fetchWithAuth(endpoint, {
            method: 'GET',
            ...options,
            headers: { ...(options.headers || {}) }
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Generic POST request
     * @param {string} endpoint - API endpoint path
     * @param {Object} data - Request body
     * @param {Object} options - Fetch options
     * @returns {Promise<any>}
     */
    async post(endpoint, data, options = {}) {
        const response = await this._fetchWithAuth(endpoint, {
            method: 'POST',
            ...options,
            headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Generic PUT request
     * @param {string} endpoint - API endpoint path
     * @param {Object} data - Request body
     * @param {Object} options - Fetch options
     * @returns {Promise<any>}
     */
    async put(endpoint, data, options = {}) {
        const response = await this._fetchWithAuth(endpoint, {
            method: 'PUT',
            ...options,
            headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Generic DELETE request
     * @param {string} endpoint - API endpoint path
     * @param {Object} options - Fetch options
     * @returns {Promise<any>}
     */
    async delete(endpoint, options = {}) {
        const response = await this._fetchWithAuth(endpoint, {
            method: 'DELETE',
            ...options,
            headers: { ...(options.headers || {}) }
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

    // ============ Утилиты ============

    getCurrentFilename() {
        return this.currentEstimateFilename;
    }

    setCurrentFilename(filename) {
        this.currentEstimateFilename = filename;
    }

    // ============================================================================
    // Estimate Lifecycle Pipeline (Sprint 19)
    // ============================================================================

    async transitionEstimateStatus(estimateId, toStatus, comment = null, isReverse = false) {
        const r = await this._fetchWithAuth(`/api/v1/estimates/${estimateId}/transition`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to_status: toStatus, comment, is_reverse: isReverse })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, status: r.status, ...json };
        return { ok: true, ...json };
    }

    async getEstimateStatusLog(estimateId) {
        const r = await this._fetchWithAuth(`/api/v1/estimates/${estimateId}/status-log`, { method: 'GET' });
        if (!r.ok) throw new Error(`getEstimateStatusLog failed: ${r.status}`);
        return (await r.json()).data;
    }

    async getEstimateOrders(estimateId) {
        const r = await this._fetchWithAuth(`/api/v1/estimates/${estimateId}/orders`, { method: 'GET' });
        if (!r.ok) throw new Error(`getEstimateOrders failed: ${r.status}`);
        return (await r.json()).data;
    }

    // ============================================================================
    // Supplier Orders (Sprint 19)
    // ============================================================================

    async listSupplierOrders(opts = {}) {
        const params = new URLSearchParams();
        if (opts.estimate_id) params.set('estimate_id', opts.estimate_id);
        if (opts.supplier_id) params.set('supplier_id', opts.supplier_id);
        if (opts.status) params.set('status', opts.status);
        params.set('limit', String(opts.limit ?? 100));
        params.set('offset', String(opts.offset ?? 0));
        const r = await this._fetchWithAuth(`/api/v1/supplier-orders?${params.toString()}`, { method: 'GET' });
        if (!r.ok) throw new Error(`listSupplierOrders failed: ${r.status}`);
        return (await r.json()).data;
    }

    async createSupplierOrder(data) {
        const r = await this._fetchWithAuth('/api/v1/supplier-orders', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`createSupplierOrder failed: ${r.status}`);
        return (await r.json()).data;
    }

    async updateSupplierOrder(id, data) {
        const r = await this._fetchWithAuth(`/api/v1/supplier-orders/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error(`updateSupplierOrder failed: ${r.status}`);
        return (await r.json()).data;
    }

    async deleteSupplierOrder(id) {
        const r = await this._fetchWithAuth(`/api/v1/supplier-orders/${id}`, { method: 'DELETE' });
        if (!r.ok) throw new Error(`deleteSupplierOrder failed: ${r.status}`);
        return (await r.json());
    }

    async sendPurchaseOrder(supplierId, opts = {}) {
        const r = await this._fetchWithAuth('/api/v1/purchase-orders/send', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                supplier_id: supplierId,
                mode: opts.mode || 'availability',
                estimate_id: opts.estimate_id,
                with_contacts: !!opts.with_contacts,
                custom_text: opts.custom_text,
                custom_subject: opts.custom_subject
            })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async previewPurchaseOrder(supplierId, opts = {}) {
        const qs = new URLSearchParams({ supplier_id: supplierId });
        if (opts.estimate_id) qs.set('estimate_id', opts.estimate_id);
        if (opts.mode) qs.set('mode', opts.mode);
        if (opts.with_contacts) qs.set('with_contacts', '1');
        const r = await this._fetchWithAuth(`/api/v1/purchase-orders/preview?${qs}`, { method: 'GET' });
        if (!r.ok) throw new Error(`previewPurchaseOrder failed: ${r.status}`);
        return (await r.json()).data;
    }
    async getPendingReconciliation() {
        const r = await this._fetchWithAuth('/api/v1/suppliers/_pending-reconciliation', { method: 'GET' });
        if (!r.ok) throw new Error(`getPendingReconciliation failed: ${r.status}`);
        return (await r.json()).data || {};
    }
    async markReconciliationDone(estimateId) {
        const r = await this._fetchWithAuth(`/api/v1/estimates/${estimateId}/reconciliation-done`, {
            method: 'POST'
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async listAllSupplierServices(opts = {}) {
        const qs = new URLSearchParams();
        if (opts.supplier_id) qs.set('supplier_id', opts.supplier_id);
        if (opts.tour_id) qs.set('tour_id', opts.tour_id);
        if (opts.status) qs.set('status', opts.status);
        const r = await this._fetchWithAuth(`/api/v1/suppliers/_all-services?${qs}`, { method: 'GET' });
        if (!r.ok) throw new Error(`listAllSupplierServices failed: ${r.status}`);
        return (await r.json()).data;
    }
    async getSupplierDashboard(supplierId) {
        const r = await this._fetchWithAuth(`/api/v1/suppliers/${supplierId}/dashboard`, { method: 'GET' });
        if (!r.ok) throw new Error(`getSupplierDashboard failed: ${r.status}`);
        return (await r.json()).data;
    }
    async getSupplierOrders(supplierId) {
        const r = await this._fetchWithAuth(`/api/v1/suppliers/${supplierId}/orders`, { method: 'GET' });
        if (!r.ok) throw new Error(`getSupplierOrders failed: ${r.status}`);
        return (await r.json()).data;
    }

    async getPipelineStats() {
        const r = await this._fetchWithAuth('/api/v1/dashboard/pipeline', { method: 'GET' });
        if (!r.ok) throw new Error(`getPipelineStats failed: ${r.status}`);
        return (await r.json()).data;
    }

    // Sprint 19c: tours (1:N estimates)
    async listTours(opts = {}) {
        const qs = new URLSearchParams();
        if (opts.client_id) qs.set('client_id', opts.client_id);
        if (opts.status) qs.set('status', opts.status);
        if (opts.limit) qs.set('limit', opts.limit);
        const r = await this._fetchWithAuth(`/api/v1/tours?${qs}`, { method: 'GET' });
        if (!r.ok) throw new Error(`listTours failed: ${r.status}`);
        return (await r.json()).data;
    }
    async getTour(id) {
        const r = await this._fetchWithAuth(`/api/v1/tours/${id}`, { method: 'GET' });
        if (!r.ok) throw new Error(`getTour failed: ${r.status}`);
        return (await r.json()).data;
    }
    async createTour(data) {
        const r = await this._fetchWithAuth('/api/v1/tours', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async updateTour(id, data) {
        const r = await this._fetchWithAuth(`/api/v1/tours/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async approveTourEstimate(tourId, estimateId, archiveOthers = true) {
        const r = await this._fetchWithAuth(`/api/v1/tours/${tourId}/approve`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ estimate_id: estimateId, archive_others: archiveOthers })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async transitionTourStatus(tourId, toStatus, comment = null) {
        const r = await this._fetchWithAuth(`/api/v1/tours/${tourId}/transition`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to_status: toStatus, comment })
        });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
    async deleteTour(id) {
        const r = await this._fetchWithAuth(`/api/v1/tours/${id}`, { method: 'DELETE' });
        const json = await r.json();
        if (!r.ok) return { ok: false, ...json };
        return { ok: true, ...json };
    }
}

// Экспорт для использования в index.html
if (typeof window !== 'undefined') {
    window.APIClient = APIClient;
}
